// Monetisation → Customers (contracts/revenue.md §4 customers, §5): every store subscription, one row each, with
// status chips and their counts, plan / store / environment filters, search by handle, player id or transaction
// id, three sorts, and "Load more". A row opens the player's Subscription tab. Everything is in the URL.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { CUSTOMER_SORTS, useCustomers, type CustomerSort } from "../../api/revenue";
import type { Subscription } from "../../api/revenueSchemas";
import { tableStyles } from "../../components/table";
import { Button, EmptyState, ErrorState, Loading, PageHeader, Segmented, Select, TextInput } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { fmtInt } from "../../lib/format";
import { fmtMoneyList, fmtUsd, planLabel, productShort, scopeText, STATUS_CHIPS, statusMeta, triggerLabel } from "../../lib/revenue";
import pages from "../pages.module.css";
import { EstimateNote, KindChip, MoneyCell, PlayerCell, ResetFilters, ScopeControls, StoreCell, SubStatusChip, useParamSetter, useScope } from "./components";
import r from "./revenue.module.css";

const SORT_LABEL: Record<CustomerSort, string> = { recent: "Recent", next_charge: "Next charge", spent: "Spent" };
const PLANS = ["monthly", "annual", "lifetime"] as const;
const day = (iso: string | null) => (iso ? formatDateTime(iso) : "—");

export function CustomersPage() {
  const scope = useScope();
  const [params, set] = useParamSetter();
  const status = params.get("status") || null;
  const plan = params.get("plan") || null;
  const sort = (CUSTOMER_SORTS as readonly string[]).includes(params.get("sort") ?? "") ? (params.get("sort") as CustomerSort) : "recent";
  const q = params.get("q") ?? "";
  const [draft, setDraft] = useState(q);
  const [synced, setSynced] = useState(q);
  if (q !== synced) {
    // The URL changed (Reset, back): the box follows it.
    setSynced(q);
    setDraft(q);
  }
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (draft.trim() !== q) set({ q: draft.trim() || null });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, q]);

  const query = { env: scope.env, store: scope.store, status, plan, q, sort };
  const list = useCustomers(query);
  const first = list.data?.pages[0];
  const counts = first?.counts ?? {};
  const all = Object.values(counts).reduce((a, b) => a + b, 0);
  const countOf = (s: string) => (s === "active" ? (counts.active ?? 0) + (counts.cancelled ?? 0) + (counts.grace ?? 0) : (counts[s] ?? 0));

  return (
    <>
      <PageHeader
        title="Customers"
        meta={
          <span>
            Every store subscription: who bought, trial or not, when it ends, what it charged, who cancelled. {scopeText(scope.env, scope.store)}. A
            row opens the player's subscription journey.
          </span>
        }
        actions={<ResetFilters />}
      />
      <div className={r.toolbar}>
        <ScopeControls scope={scope} />
        <Select value={plan ?? ""} onChange={(e) => set({ plan: e.target.value || null })} aria-label="Plan">
          <option value="">All plans</option>
          {PLANS.map((p) => (
            <option key={p} value={p}>
              {planLabel(p)}
            </option>
          ))}
        </Select>
        <Segmented label="Sort" value={sort} options={CUSTOMER_SORTS.map((s) => ({ value: s, label: SORT_LABEL[s] }))} onChange={(v) => set({ sort: v === "recent" ? null : v })} />
        <TextInput
          data-search
          className={r.search}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="@handle, player id or transaction id   ( / )"
          aria-label="Search customers"
        />
      </div>

      <div className={r.chips} role="group" aria-label="Status" data-testid="status-chips">
        <button type="button" className={r.chipButton} aria-pressed={status === null} onClick={() => set({ status: null })}>
          All <span className={r.count}>{first ? fmtInt(all) : "…"}</span>
        </button>
        {STATUS_CHIPS.map((c) => (
          <button
            key={c.value}
            type="button"
            className={r.chipButton}
            aria-pressed={status === c.value}
            data-status={c.value}
            title={c.value === "active" ? "Active also counts cancelled and grace: everyone in a paid period with Pro" : statusMeta(c.value).hint}
            onClick={() => set({ status: status === c.value ? null : c.value })}
          >
            <span className={r.chipDot} data-tone={statusMeta(c.value).tone} aria-hidden />
            {c.label} <span className={r.count}>{first ? fmtInt(countOf(c.value)) : "…"}</span>
          </button>
        ))}
      </div>

      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.isPending ? (
        <Loading rows={10} />
      ) : (
        <CustomersTable pages={list.data.pages} fetching={list.isFetching && !list.isFetchingNextPage} />
      )}
      {list.hasNextPage ? (
        <div className={pages.loadMore}>
          <Button size="sm" onClick={() => void list.fetchNextPage()} busy={list.isFetchingNextPage} data-testid="customers-more">
            Load more
          </Button>
        </div>
      ) : null}
      <EstimateNote />
    </>
  );
}

function CustomersTable({ pages: list, fetching }: { pages: { items: Subscription[]; total: number }[]; fetching: boolean }) {
  const navigate = useNavigate();
  const items = list.flatMap((p) => p.items);
  const total = list[0]?.total ?? items.length;
  if (items.length === 0) return <EmptyState title="No customers match">Try another status, plan or store, or the All environment.</EmptyState>;
  return (
    <>
      <div className={tableStyles.wrap} aria-busy={fetching}>
        <table className={tableStyles.table} data-testid="customers">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Product</th>
              <th>Store</th>
              <th>Status</th>
              <th>Started</th>
              <th>Trial</th>
              <th>Renews or ends</th>
              <th data-align="right">Next charge</th>
              <th data-align="right">Paid</th>
              <th data-align="right">Spent</th>
              <th>Last event</th>
              <th>Paywall</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
                  <tr
                    key={s.id}
                    data-testid="customer-row"
                    data-status={s.status}
                    data-clickable={s.userId ? true : undefined}
                    onClick={(e) => s.userId && !(e.target as HTMLElement).closest("a") && navigate(`/players/${s.userId}/subscription`)}
                  >
                    <td>
                      <PlayerCell userId={s.userId} handle={s.handle} country={s.country} />
                    </td>
                    <td>
                      {productShort(s.productId, s.plan)}
                      <span className={r.sub}>{s.productId}</span>
                    </td>
                    <td>
                      <StoreCell store={s.store} env={s.environment} />
                    </td>
                    <td>
                      <SubStatusChip status={s.status} endReason={s.endReason} />
                    </td>
                    <td className="mono">{day(s.startedAt)}</td>
                    <td>
                      <TrialCell s={s} />
                    </td>
                    <td>
                      <PeriodCell s={s} />
                    </td>
                    <td data-align="right">
                      {s.nextCharge ? (
                        <span className={r.money} data-align="right">
                          <MoneyCell price={s.nextCharge.price} usd={s.nextCharge.usd} align="right" />
                          <span className={r.sub}>{day(s.nextCharge.at)}</span>
                        </span>
                      ) : (
                        <span className={pages.faint}>—</span>
                      )}
                    </td>
                    <td data-align="right" className="mono">
                      {fmtInt(s.paidPeriods)}
                    </td>
                    <td data-align="right">
                      <span className={r.money} data-align="right">
                        <span className="mono">{fmtUsd(s.spentUsd)}</span>
                        {s.spent.some((m) => m.currency !== "USD") ? <span className={r.sub}>{fmtMoneyList(s.spent)}</span> : null}
                      </span>
                    </td>
                    <td>
                      {s.lastEvent ? (
                        <span className={r.statusCell}>
                          <KindChip kind={s.lastEvent.kind} />
                          <span className={r.sub}>{day(s.lastEvent.at)}</span>
                        </span>
                      ) : (
                        <span className={pages.faint}>—</span>
                      )}
                    </td>
                    <td>
                      {s.attribution ? (
                        <>
                          {triggerLabel(s.attribution.trigger)}
                          {s.attribution.variant ? <span className={r.sub}>variant {s.attribution.variant.toUpperCase()}</span> : null}
                        </>
                      ) : (
                        <span className={pages.faint}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      <p className={r.faintLine} data-testid="customers-count">
        {fmtInt(items.length)} of {fmtInt(total)} shown
      </p>
    </>
  );
}

function TrialCell({ s }: { s: Subscription }) {
  if (!s.trialEndsAt) return <span className={pages.faint}>no trial</span>;
  const over = Date.parse(s.trialEndsAt) <= Date.now() || s.converted !== null;
  return (
    <span className={r.statusCell}>
      <span className="mono">
        {over ? "ended" : "ends"} {day(s.trialEndsAt)}
      </span>
      <span className={r.sub}>{s.converted === true ? "converted" : s.converted === false ? "not converted" : "in trial"}</span>
    </span>
  );
}

function PeriodCell({ s }: { s: Subscription }) {
  const end = s.currentPeriodEndsAt;
  let text: string;
  let sub: string | null = null;
  switch (s.status) {
    case "lifetime":
      text = "never";
      break;
    case "trial":
    case "active":
      text = end ? `renews ${day(end)}` : "renews";
      break;
    case "trial_cancelled":
    case "cancelled":
      text = end ? `ends ${day(end)}` : "ends";
      sub = s.cancelledAt ? `cancelled ${day(s.cancelledAt)}` : null;
      break;
    case "grace":
      text = s.graceEndsAt ? `grace until ${day(s.graceEndsAt)}` : "in grace";
      sub = end ? `failed ${day(end)}` : null;
      break;
    case "billing_retry":
      text = "retrying";
      sub = end ? `failed ${day(end)}` : null;
      break;
    case "refunded":
    case "revoked":
      text = statusMeta(s.status).label;
      sub = s.lastEvent ? day(s.lastEvent.at) : null;
      break;
    default:
      text = end ? `ended ${day(end)}` : "ended";
      sub = s.cancelledAt ? `cancelled ${day(s.cancelledAt)}` : null;
  }
  return (
    <span className={r.statusCell}>
      <span className="mono">{text}</span>
      {sub ? <span className={r.sub}>{sub}</span> : null}
    </span>
  );
}
